import { Popper } from '@material-ui/core'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import classNames from 'classnames'
import { permissionsSelectors } from 'ducks/permissions'
import { uniqBy } from 'lodash'
import { SellerIntegrationType } from 'opensolar-common'
import { Box, Typography } from 'opensolar-ui'
import useEnabledHardwareSuppliers from 'pages/ordering/hooks/useEnabledHardwareSuppliers'
import { useGetSupplierConfig } from 'pages/ordering/hooks/useGetSupplierConfig'
import { CostSource, HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import { useFeatureFlag } from 'util/featureFlags'
import { DistributorTradePricingContext, DistributorTradePricingContextType } from '../HardwareSelectorCore'
import { InverterWizardComponentType } from '../inverterWizard/types'
import { DistributorDataTypeV2, HardwareComponentType } from '../types'
import DistributorChipSelector from './DistributorChipSelector'

type ComponentType = HardwareComponentType | InverterWizardComponentType

/***
 * Note that the filename says DistributorBlockV2, but we commonly refer to the functionality as 3.0.
 */

const useStyles = makeOpenSolarStyles((theme) => ({
  pricingSource: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    color: '#6B6A76',
  },
  wrapper: {
    padding: '4px 8px',
    textAlign: 'left',
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    cursor: 'pointer',
    boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.25)',
    minWidth: '70%',
  },
  popperOptionLabel: {
    fontSize: 10,
    fontWeight: 600,
    lineHeight: '12px',
    color: '#757575',
    paddingLeft: 20,
  },
  selector: {
    border: '1px solid #E6E6E6',
    borderRadius: 4,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px',
    cursor: 'pointer',
    width: '80%',
    minHeight: 28,
  },
  supplierWrapper: {
    padding: '8px',
  },
  supplierName: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
  },
  selectedSupplierName: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    width: '75%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  placeholder: {
    color: '#ADACB0',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
  },
  supplierImg: {
    width: 50,
    height: 32,
    marginRight: 8,
    border: '1px solid #E6E6E6',
    borderRadius: 4,
  },
}))

const SupplierOption = ({
  supplier,
  onSupplierSelected,
}: {
  supplier: SellerIntegrationType | undefined
  onSupplierSelected: () => void
}) => {
  const classes = useStyles()

  return (
    <div className={classes.supplierWrapper} onClick={onSupplierSelected}>
      <div style={{ display: 'inline-flex', alignContent: 'center', alignItems: 'center' }}>
        <div className={classes.supplierImg}>
          <img src={supplier?.logoUrl || ''} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
        <Typography className={classes.supplierName}>{supplier?.name}</Typography>
      </div>
    </div>
  )
}

interface DistributorBlockV2Props {
  distributors: DistributorDataTypeV2[]
  onUpdateCostBySource: (source: CostSource | undefined, price: number) => void
  component: ComponentType
}

const DistributorBlockV2: React.FC<DistributorBlockV2Props> = ({ distributors, onUpdateCostBySource, component }) => {
  const [costSource, setCostSource] = useState<CostSource | undefined>()
  const [supplierAnchorEl, setSupplierAnchorEl] = useState<HTMLElement | null>(null)
  const multiDistributor = useFeatureFlag('design_multi_distributor', 'on')
  const { filteredSuppliers: enabledDistributors } = useEnabledHardwareSuppliers()
  const { getSupplierConfig } = useGetSupplierConfig()
  const distributorTradePricing = useContext<DistributorTradePricingContextType>(DistributorTradePricingContext)
    .distributorTradePricing
  const filteredDistributors = uniqBy(
    distributors.filter((item: DistributorDataTypeV2) => enabledDistributors.includes(item.distributor)),
    'distributor'
  )
  const classes = useStyles()
  const hardwareSourceSelectionPermission = useSelector((state) =>
    permissionsSelectors.getPermissionByKey(state, 'hardware_source_selection')
  )

  const updateCostSourceForComponent = (costSource: CostSource | undefined) => {
    if (!costSource) {
      onUpdateCostBySource(undefined, 0)
    } else if (costSource === 'component') {
      onUpdateCostBySource(costSource, (component as any)?.cost ?? 0)
    } else {
      const selectedHardware = filteredDistributors.find((pricing) => pricing.distributor === costSource)
      onUpdateCostBySource(costSource, selectedHardware?.price || 0)
    }
  }

  const handleSelectSource = (selectedCostSource: CostSource) => {
    setCostSource(selectedCostSource === costSource ? undefined : selectedCostSource)
    updateCostSourceForComponent(selectedCostSource === costSource ? undefined : selectedCostSource)
    setSupplierAnchorEl(null)
  }

  useEffect(() => {
    if (distributors.length === 0) {
      updateCostSourceForComponent('component')
    }
  }, [distributors])

  return (
    <Box alignItems="center" display="flex" flexDirection="row" flexWrap="wrap" gridGap={6}>
      {multiDistributor ? (
        <DistributorChipSelector
          distributors={filteredDistributors}
          componentActivationCost={(component as any)?.cost ?? 0}
          costSource={costSource}
          component={component}
          onClickDistributor={(costSource: CostSource) => {
            if (hardwareSourceSelectionPermission.allowEdit) {
              handleSelectSource(costSource)
            }
          }}
        />
      ) : (
        <>
          <span className={classes.pricingSource}>{`${filteredDistributors.length + 1} pricing sources`}</span>
          <div
            className={classes.selector}
            style={Boolean(supplierAnchorEl) ? { border: '2px solid #4272DD', padding: '7px 8px' } : {}}
            onClick={(e) => setSupplierAnchorEl(!supplierAnchorEl ? e.currentTarget : null)}
          >
            {costSource ? (
              costSource === 'component' ? (
                <span className={classes.selectedSupplierName}>Manual (Business Control Settings)</span>
              ) : (
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <img
                    style={{ height: 28 }}
                    src={getSupplierConfig(costSource as HardwareSupplierFilterKeyType)?.logoUrl}
                  />
                  <span style={{ marginLeft: 8 }}>
                    {getSupplierConfig(costSource as HardwareSupplierFilterKeyType)?.name}
                  </span>
                </div>
              )
            ) : (
              <span className={classes.placeholder}>Select Source</span>
            )}
            <KeyboardArrowDownIcon htmlColor="#6B6A76" />
          </div>
          <Popper
            open={Boolean(supplierAnchorEl)}
            anchorEl={supplierAnchorEl}
            style={{ zIndex: 2000 }}
            placement="bottom-start"
            disablePortal
          >
            <div className={classes.wrapper}>
              {filteredDistributors.map((distributor) => {
                const supplierConfig = getSupplierConfig(distributor.distributor)
                if (supplierConfig) {
                  return (
                    <SupplierOption
                      supplier={supplierConfig}
                      onSupplierSelected={() => {
                        handleSelectSource(supplierConfig.filterKey as CostSource)
                      }}
                    />
                  )
                } else return null
              })}
              <div
                className={classNames(classes.supplierWrapper, classes.supplierName)}
                onClick={() => {
                  handleSelectSource('component')
                }}
              >
                Manual (Design & Hardware Control Settings)
              </div>
            </div>
          </Popper>
        </>
      )}
    </Box>
  )
}

export default DistributorBlockV2
